/**
 * Patient Summary helpers. Problem List and Medical History stay separate.
 * Neither card is populated from Encounter.reasonCode or NoteVersion.diagnoses[].
 */
import { ALLERGIES_NKA } from "@/lib/allergyCatalog";
import { listMedicalHistory } from "@/lib/pmhApi";
import { listPatientAllergies } from "@/lib/patientAllergyApi";
import { listSurgicalHistory } from "@/lib/surgicalHistoryApi";
import { hydrateProblemList } from "@/lib/diagnosisSources";
import {
  conditionsFromItems,
  formatPmhDiagnosis,
  negativesFromItems,
} from "@/lib/pmhCatalog";
import { formatPxDisplay } from "@/lib/surgicalHistoryCatalog";

export const PROBLEM_LIST_EMPTY = "No active problems recorded.";
export const MEDICAL_HISTORY_EMPTY = "No medical history recorded.";
export const SURGICAL_HISTORY_EMPTY = "No surgical history recorded.";
export { ALLERGIES_EMPTY, ALLERGIES_NKA } from "@/lib/allergyCatalog";

const LIST_JOIN = "; ";

function isActiveFlag(value) {
  const status = String(value || "active").toLowerCase();
  return status === "active";
}

function skipPlaceholderDash(label) {
  return Boolean(label) && label !== "—";
}

export function emptyChartPlaceholderLists() {
  return {
    medicalHistory: [],
    surgicalHistory: [],
    allergies: [],
    allergyReview: null,
    noKnownAllergies: false,
    problems: [],
  };
}

/** Active PMH conditions using the chart diagnosis label `description (code)`. */
export function formatPmhPlaceholder(items = []) {
  return conditionsFromItems(items)
    .filter((item) => isActiveFlag(item?.status))
    .map((item) => formatPmhDiagnosis(item))
    .filter(skipPlaceholderDash)
    .join(LIST_JOIN);
}

/** Active surgical-history rows using the chart procedure label. */
export function formatSurgicalHistoryPlaceholder(items = []) {
  return (items || [])
    .filter((item) => isActiveFlag(item?.recordStatus))
    .map((item) => formatPxDisplay(item))
    .filter(skipPlaceholderDash)
    .join(LIST_JOIN);
}

/** Vocabulary @allergies@ copy when NKDA is attested and no active allergy names exist. */
export const ALLERGIES_PLACEHOLDER_NKDA = "NKDA";

function isNkdaAttested({ noKnownAllergies, allergyReview } = {}) {
  return noKnownAllergies === true || allergyReview?.noKnownAllergies === true;
}

/**
 * Active allergy display names for @allergies@.
 * If none exist and NKDA is explicitly attested, return exactly `NKDA`.
 * Do not use the sentence reserved for @nkda@.
 */
export function formatAllergiesPlaceholder(items = [], review = {}) {
  const names = (items || [])
    .filter((item) => isActiveFlag(item?.clinicalStatus))
    .map((item) => String(item?.allergenDisplayName || item?.displayName || "").trim())
    .filter(Boolean)
    .join(LIST_JOIN);
  if (names) return names;
  return isNkdaAttested(review) ? ALLERGIES_PLACEHOLDER_NKDA : "";
}

/** Vocabulary @nkda@: attested no-known-allergies copy, else empty. */
export function nkdaPlaceholderText({ noKnownAllergies, allergyReview } = {}) {
  return isNkdaAttested({ noKnownAllergies, allergyReview }) ? ALLERGIES_NKA : "";
}

/** Problem List `CODE — description`, matching SOAP @diagnoses@ list style. */
export function formatProblemsPlaceholder(items = []) {
  return (items || [])
    .map((item) => {
      const code = item?.code || "";
      const description = item?.description || item?.display || "";
      if (code && description) return `${code} — ${description}`;
      return description || code || "";
    })
    .filter(Boolean)
    .join(LIST_JOIN);
}

export async function loadChartPlaceholderLists(patientId) {
  const empty = emptyChartPlaceholderLists();
  if (!patientId) return empty;
  const [pmh, surgical, allergies, problems] = await Promise.all([
    loadSummaryMedicalHistory(patientId).catch(() => ({ items: [] })),
    loadSummarySurgicalHistory(patientId).catch(() => ({ items: [] })),
    loadSummaryAllergies(patientId).catch(() => ({
      items: [],
      review: null,
      noKnownAllergies: false,
    })),
    loadSummaryProblemList(patientId).catch(() => []),
  ]);
  return {
    medicalHistory: pmh.items || [],
    surgicalHistory: surgical.items || [],
    allergies: allergies.items || [],
    allergyReview: allergies.review || null,
    noKnownAllergies: Boolean(allergies.noKnownAllergies || allergies.review?.noKnownAllergies),
    problems: problems || [],
  };
}

export async function loadSummaryProblemList(patientId) {
  return hydrateProblemList(patientId);
}

export async function loadSummaryMedicalHistory(patientId) {
  const data = await listMedicalHistory(patientId);
  return {
    items: data.items || [],
    conditions: conditionsFromItems(data.items),
    negatives: negativesFromItems(data.items),
    review: data.review || null,
  };
}

export async function loadSummarySurgicalHistory(patientId) {
  const data = await listSurgicalHistory(patientId);
  return {
    items: data.items || [],
    review: data.review || null,
  };
}

export async function loadSummaryAllergies(patientId) {
  const data = await listPatientAllergies(patientId);
  return {
    items: data.items || [],
    review: data.review || null,
    noKnownAllergies: !!data.noKnownAllergies,
  };
}

export function formatSummaryProblem(item) {
  const code = item?.code || "";
  const description = item?.description || item?.display || "";
  if (code && description) return { code, description };
  if (description) return { code: "", description };
  return { code, description: code };
}

export function formatSummaryHistory(item) {
  return formatSummaryProblem({
    code: item?.code || "",
    description: item?.display || item?.description || "",
  });
}

export function usesEncounterOrNoteDiagnoses() {
  return false;
}

export function shouldDeduplicateProblemListAgainstPmh() {
  return false;
}
